// Intent-matched SEO/AEO landing pages. Each page leads with a direct-answer
// paragraph (the passage AI engines quote) and maps ONLY to homes that truly
// match the claim — no thin/duplicate boilerplate, every statement accurate.

// Cruise lines homeporting at Port Everglades (2026 season; terminals can
// change by sailing — the outro says so). Shared by both cruise landing pages.
const CRUISE_LINES_SECTION = {
  h: "Cruise lines sailing from Port Everglades",
  intro:
    "One of the busiest cruise ports on earth — if you're sailing from Fort Lauderdale, your line is almost certainly here. Each line has its usual terminal:",
  lines: [
    { name: "Royal Caribbean", terminal: "Terminal 18", note: "Year-round Caribbean sailings, including the Oasis-class giants." },
    { name: "Celebrity Cruises", terminal: "Terminal 25", note: "Premium Edge-class ships from the port's glass-walled terminal." },
    { name: "Princess Cruises", terminal: "Terminal 2", note: "Caribbean favorites, Panama Canal and longer voyages." },
    { name: "Holland America Line", terminal: "Terminal 26", note: "The port's heritage line — classic Caribbean and Panama Canal cruises." },
    { name: "Disney Cruise Line", terminal: "Terminal 4", note: "A dedicated family terminal — Disney's second Florida homeport." },
    { name: "Carnival Cruise Line", terminal: "Terminals 19 & 21", note: "Value-packed fun-ship Caribbean getaways." },
  ],
  outro:
    "Terminals can change by sailing — check your cruise documents. We'll happily point you to the right one at check-in.",
};

// Cruise-specific per-page meta keywords (the keywords tag is legacy — ignored
// by Google/AI since 2009 — but the owner asked for it; harmless).
const CRUISE_KEYWORDS =
  "pre-cruise hotel Fort Lauderdale, Port Everglades hotel, cruise port accommodation Fort Lauderdale, where to stay before a cruise Fort Lauderdale, Port Everglades vacation rental, stay near Port Everglades, Royal Caribbean Port Everglades, Celebrity Cruises Port Everglades, Disney Cruise Line Port Everglades, pet friendly pre-cruise stay";

// Short urgency line rendered above the final CTA on the cruise pages.
const CRUISE_CTA_NOTE =
  "Port Everglades peak season runs November through April — dates around weekend departures fill first.";

// Shared "when to book" urgency callout for the cruise pages. No invented
// discounts — it reinforces the real book-direct best-rate promise.
const WHEN_TO_BOOK = {
  h: "When to book",
  body: "Peak season at Port Everglades runs November through April, and the nights before weekend departures go first. Our advice: lock in your dates as soon as your cruise is booked — and book direct. Our best rates are always right here, and a day or two on either side of your sailing is the easiest upgrade to the whole trip.",
};

// REAL guest reviews chosen for what cruise travelers care about (location,
// logistics, responsive hosts). IMPORTANT: only genuine reviews — when a guest
// mentions a cruise/Port Everglades in a review, swap it in here (that's the
// whole point of this block). Never invent a cruise review.
const CRUISE_GUEST_REVIEWS = [
  {
    name: "Emily",
    date: "November 2025",
    text: "PERFECT for a weekend getaway, with everything we needed within walking distance — a cute cafe, a Starbucks and a store. A short drive to the beach and other attractions too.",
  },
  {
    name: "Aaron",
    date: "May 2026",
    text: "Stylish, immaculate and steps from the nightlife. The host replied within minutes every single time. Highly recommend.",
  },
];

// Shared "choose your rental" section for the pet-friendly pages (all four homes welcome pets).
const PET_FRIENDLY_DIFFS = {
  h: "What Makes Double Rainbow Suites Pet-Friendly?",
  body: "No surprise fees or awkward rules — add your pet during booking and the home is ready for the whole family. Real differentiators, not generic dog-friendly fluff:",
  list: [
    "Dogs and cats welcome",
    "Private homes and suites — not a hotel room",
    "Free parking",
    "Full kitchen in the Private Pool Oasis",
    "Private outdoor space",
    "Heated private pool at the Fort Lauderdale home",
    "About 10 minutes from Fort Lauderdale Beach",
    "Wilton Drive and pet-friendly patios nearby",
    "Level 2 EV charging at the Wilton Manors homes — free for guests",
  ],
};

const PET_FRIENDLY_CHOOSE = {
  h: "Choose Your Pet-Friendly Vacation Rental",
  items: [
    {
      slug: "pet-friendly-pool-oasis",
      name: "Private Pool Oasis",
      stats: "Sleeps 6 · 2 bedrooms · 2 bathrooms",
      blurb: "Heated private pool, fenced outdoor space, full kitchen, king bed, free parking and plenty of room for the whole family — including the four-legged members.",
    },
    {
      slug: "wilton-king-suite-north",
      name: "King Suite — North",
      stats: "Sleeps 2 · 1 bedroom · 1 bathroom",
      blurb: "A private king suite within walking distance of Wilton Drive, with its own outdoor space and everything you need for a comfortable stay with your dog or cat.",
    },
    {
      slug: "wilton-king-suite-south",
      name: "King Suite — South",
      stats: "Sleeps 2 · 1 bedroom · 1 bathroom",
      blurb: "A second private king suite near Wilton Drive, ideal for couples traveling with a pet who want nightlife, restaurants and cafés within easy walking distance.",
    },
    {
      slug: "spacious-2br-efficiencies",
      name: "2BR Efficiencies",
      stats: "Sleeps 4 · 2 bedrooms · 2 bathrooms",
      blurb: "Two king beds, more space for families or friends, and a convenient Wilton Manors location.",
    },
  ],
};

const vetCareData = {
  heading: "Visiting a Fort Lauderdale vet specialist with your pet?",
  intro:
    "Plenty of our guests book a suite while their best friend sees a specialist nearby. Every Double Rainbow Suites home is pet-friendly — dogs and cats welcome — with self check-in for early appointments and private outdoor space for recovery days. These are the vets our guests know best:",
  vets: [
    {
      name: "BluePearl Pet Hospital — Fort Lauderdale",
      url: "https://bluepearlvet.com/hospital/fort-lauderdale-fl/",
      distance: "about a 10-minute drive",
      detail:
        "the area's 24/7 emergency and specialty referral hospital (formerly Lauderdale Veterinary Specialists) — surgery, oncology, neurology and more",
    },
    {
      name: "Veterinary Emergency Group — Fort Lauderdale",
      url: "https://veg.com/locations/florida/fort-lauderdale",
      distance: "about a 15-minute drive",
      detail: "24/7 emergency-only hospital with an open floor plan, so you can stay right beside your pet through treatment",
    },
    {
      name: "Oakland Park Animal Hospital",
      url: "https://oaklandparkanimalhospital.com",
      distance: "about an 8-minute drive",
      detail: "full-service neighborhood vet for wellness, dentistry and same-day urgent care — the closest option to our homes",
    },
    {
      name: "VCA Imperial Point Animal Hospital",
      url: "https://vcahospitals.com/imperial-point",
      distance: "about a 10-minute drive",
      detail: "general practice with urgent-care hours, east toward the beach",
    },
  ],
  closing:
    "And if your pet ever needs care mid-stay, you're minutes from all of them — just ask us and we'll point you in the right direction.",
};

export const landingPages = [
  {
    slug: "fort-lauderdale-pet-friendly-vacation-rentals",
    category: "Pet-friendly · Fort Lauderdale",
    ogImage: "/og-image-pet-friendly.jpg",
    h1: "Pet-Friendly Vacation Rentals in Fort Lauderdale",
    metaTitle: "Pet-Friendly Vacation Rentals in Fort Lauderdale | Double Rainbow Suites",
    metaDescription:
      "Bringing the dog to Fort Lauderdale? Double Rainbow Suites offers pet-friendly vacation rentals — including a private-pool home — minutes from Wilton Manors and Fort Lauderdale Beach. Book direct with self check-in.",
    keywords:
      "pet friendly vacation rentals fort lauderdale, pet friendly rentals wilton manors, dog friendly vacation rental fort lauderdale, pet friendly airbnb alternative fort lauderdale, vacation rental with private pool pet friendly",
    answer:
      "Looking for pet-friendly vacation rentals in Fort Lauderdale? Double Rainbow Suites offers pet-friendly vacation homes and king suites in the Wilton Manors area — close to Wilton Drive, Fort Lauderdale beaches, downtown Fort Lauderdale and the local restaurants and nightlife. Choose from a private-pool home that sleeps six or comfortable king suites, all with free parking, self check-in and easy access to everything South Florida has to offer. Every home welcomes dogs and cats — simply add your pet when you book, with no platform fees when you book direct.",
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    chooseRentals: PET_FRIENDLY_CHOOSE,
    petPhotoStrip: true,
    vetCare: vetCareData,
    context: [
      {
        h: "Why Fort Lauderdale is great with a dog",
        body:
          "Fort Lauderdale is one of South Florida's most dog-friendly cities, with the leash-friendly stretch of Fort Lauderdale Beach, waterfront parks and dozens of patios that welcome pets. Our private-pool home gives your dog a fenced yard to roam and a heated pool for you — all about a 10-minute drive from the beach and the nightlife of Wilton Drive.",
      },
      {
        h: "Dog-friendly things to do nearby",
        body: "The dog days here are the good kind:",
        list: [
          "Canine Beach — the city's designated dog beach at the north end of Fort Lauderdale Beach (a small permit applies at weekends; check the city's current rules before you go).",
          "Snyder Park — home to a beloved dog-swim lake where pups cool off off-leash.",
          "Hugh Taylor Birch State Park — leashed dogs welcome on the trails, right off A1A.",
          "The Riverwalk — an easy riverside stroll through downtown, any evening.",
        ],
      },
      {
        h: "Patios, parks and Wilton Drive",
        body: "Closer to the suites, Wilton Manors does dog-friendly better than anywhere:",
        list: [
          "Rosie's Bar & Grill — the famous dog-friendly patio on Wilton Drive.",
          "Wilton Drive's café and bar patios — water bowls out front are the norm, not the exception.",
          "Colohatchee Park — Wilton Manors' own park with a dedicated dog area.",
          "Holiday Park — big open spaces and a dog park, a few minutes' drive away.",
        ],
      },
      {
        h: "Our pet policy, plainly",
        body:
          "Dogs and cats are welcome at every one of our homes, with no restrictions — simply add your pet to the reservation when you book. A small pet fee applies per pet, per stay and varies a little by home; the exact amount is always shown at checkout before you book, never on arrival. Every home has private outdoor space, self check-in means no awkward lobby moments, and booking direct means no platform fees on top.",
      },
      PET_FRIENDLY_DIFFS,
    ],
    faqs: [
      {
        q: "Are Double Rainbow Suites rentals in Fort Lauderdale pet-friendly?",
        a: "Yes — all four of our homes are pet-friendly: the Private Pool Oasis in Fort Lauderdale, and the King Suite North, King Suite South and 2BR Efficiencies in Wilton Manors. Dogs and cats are welcome with no restrictions — simply add your pet to the reservation when you book. A small fee per pet, per stay applies, and the exact amount is always shown at checkout before you book.",
      },
      {
        q: "What is Double Rainbow Suites' pet policy?",
        a: "Dogs and cats are welcome at every home, no restrictions. Add your pet during booking — a small fee per pet, per stay applies and the exact amount is shown at checkout. Every home has private outdoor space, and the Wilton Manors suites are a short walk from Wilton Drive's pet-friendly patios.",
      },
      {
        q: "Is there a pet fee?",
        a: "Yes — a small fee per pet, per stay. It varies a little by home and the exact amount is always shown at checkout before you book, so there are never surprises on arrival.",
      },
      {
        q: "Is there a dog beach near the rentals?",
        a: "Yes — Fort Lauderdale's designated Canine Beach sits at the north end of the beach, about 10 minutes away (a small weekend permit applies). Snyder Park's dog-swim lake and the trails at Hugh Taylor Birch State Park are close by too.",
      },
      {
        q: "How close is it to Fort Lauderdale Beach?",
        a: "The home is roughly 2.5 miles — about a 10-minute drive — from Fort Lauderdale Beach, and a similar distance from Wilton Drive.",
      },
    ],
  },
  {
    slug: "wilton-manors-pet-friendly",
    category: "Pet-friendly · Wilton Manors",
    ogImage: "/og-image-pet-friendly.jpg",
    h1: "Pet-Friendly Vacation Rentals in Wilton Manors",
    metaTitle: "Pet-Friendly Vacation Rentals in Wilton Manors | Double Rainbow Suites",
    metaDescription:
      "Pet-friendly king suites in the heart of Wilton Manors, a short walk from Wilton Drive. Dog-friendly, self check-in, book direct with Double Rainbow Suites.",
    answer:
      "Double Rainbow Suites offers pet-friendly vacation rentals in Wilton Manors — private king suites a short walk from Wilton Drive's bars, restaurants and nightlife. Every suite welcomes dogs and cats, with self check-in and direct booking, making it easy to bring your pet to Greater Fort Lauderdale's most walkable, LGBTQ+ neighborhood.",
    propertySlugs: ["wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    chooseRentals: PET_FRIENDLY_CHOOSE,
    petPhotoStrip: true,
    vetCare: vetCareData,
    context: [
      {
        h: "A walkable, dog-loving town",
        body:
          "Wilton Manors is made for walking — and for dogs. From our king suites you can stroll to Wilton Drive in minutes, and you'll find green space, water bowls at café patios and a genuinely pet-welcoming community throughout the neighborhood.",
      },
      {
        h: "Dog-friendly things to do in Wilton Manors",
        body: "Everything on this list is a short walk or a few minutes' drive from the suites:",
        list: [
          "Colohatchee Park — the neighborhood's own park, with a dedicated dog area and a boardwalk along the Middle River.",
          "Rosie's Bar & Grill — the famous dog-friendly patio; your pup gets greeted before you do.",
          "Wilton Drive's patios — water bowls out front are the norm up and down the strip.",
          "Canine Beach — Fort Lauderdale's designated dog beach, about 10–15 minutes by car (a small weekend permit applies).",
          "Snyder Park's dog-swim lake — the off-leash cooldown locals swear by.",
        ],
      },
      {
        h: "Suites built for couples (and their dog)",
        body:
          "Our Wilton Manors homes range from intimate king suites for two to a two-bedroom that sleeps four. Each is pet-friendly with a private outdoor space, fast Wi-Fi, self check-in and a quiet retreat to come home to after a night on the Drive.",
      },
      {
        h: "Our pet policy, plainly",
        body:
          "Dogs and cats are welcome at all three Wilton Manors homes at 1425 NE 24th St, with no restrictions — simply add your pet to the reservation when you book. A small fee per pet, per stay applies; the exact amount is shown at checkout before you book, never on arrival. Each home has its own private outdoor space, self check-in means no awkward lobby moments with a nervous pup, and booking direct means no platform fees on top.",
      },
      PET_FRIENDLY_DIFFS,
    ],
    faqs: [
      {
        q: "Which Wilton Manors rentals are pet-friendly?",
        a: "All of our Wilton Manors homes are pet-friendly — the King Suite North, the King Suite South and the Spacious 2BR Efficiencies. Dogs and cats are welcome with no restrictions; add your pet when you book and a small fee per pet, per stay is shown at checkout.",
      },
      {
        q: "What is Double Rainbow Suites' pet policy?",
        a: "Dogs and cats are welcome at every home, no restrictions — add your pet during booking and a small fee per pet, per stay is shown at checkout. Every Wilton Manors home has private outdoor space, and Wilton Drive's pet-friendly patios are a short walk away.",
      },
      {
        q: "Can you walk to Wilton Drive with a dog?",
        a: "Yes. Our king suites are a short walk from Wilton Drive, and the neighborhood is very walkable and dog-friendly.",
      },
      {
        q: "Is there a dog park in Wilton Manors?",
        a: "Yes — Colohatchee Park has a dedicated dog area a few minutes from our suites, and Holiday Park's dog park is a short drive. For beach days, Fort Lauderdale's designated Canine Beach is about 10–15 minutes away.",
      },
    ],
  },
  {
    slug: "fort-lauderdale-vacation-rentals-with-pool",
    category: "Private pool · Fort Lauderdale",
    h1: "Fort Lauderdale Vacation Rentals With a Private Pool",
    metaTitle: "Fort Lauderdale Vacation Rental With Private Pool | Double Rainbow Suites",
    metaDescription:
      "Want a private pool in Fort Lauderdale? Our pet-friendly Private Pool Oasis features a heated pool, sleeps 6, and sits minutes from the beach and Wilton Drive. Book direct.",
    answer:
      "Double Rainbow Suites offers a private-pool vacation rental in Fort Lauderdale — the Stylish Pet Friendly Home with Private Pool Oasis — featuring a heated private pool, sleeping up to six, and located about 10 minutes from Fort Lauderdale Beach and Wilton Drive. It's pet-friendly and bookable directly with self check-in.",
    propertySlugs: ["pet-friendly-pool-oasis"],
    context: [
      {
        h: "Your own heated pool, in complete privacy",
        body:
          "Swim on your own schedule. The Private Pool Oasis has a heated private pool and patio with loungers — no sharing with other guests, no resort crowds — a favorite for families and groups who want an outdoor retreat of their own.",
      },
      {
        h: "Central, yet tucked away",
        body:
          "The home sleeps six across two bedrooms and two bathrooms with a full kitchen, and it's pet-friendly. You're roughly a 10-minute drive from both Fort Lauderdale Beach and the nightlife of Wilton Drive — central to everything, yet a private, quiet escape.",
      },
    ],
    faqs: [
      {
        q: "Does Double Rainbow Suites have a rental with a private pool?",
        a: "Yes — the Stylish Pet Friendly Home with Private Pool Oasis in Fort Lauderdale has a heated private pool and sleeps up to six.",
      },
      {
        q: "Is the pool heated, and is the home pet-friendly?",
        a: "Yes to both. The pool is heated for year-round swimming, and the home is pet-friendly — bring your dog or cat.",
      },
    ],
  },
  {
    slug: "wilton-manors-vacation-rentals",
    category: "Stays · Wilton Manors",
    h1: "Vacation Rentals in Wilton Manors",
    metaTitle: "Vacation Rentals in Wilton Manors, FL | Double Rainbow Suites",
    metaDescription:
      "Boutique vacation rentals in Wilton Manors — private king suites a short walk from Wilton Drive. LGBTQ+ owned, pet-friendly, self check-in, book direct.",
    answer:
      "Double Rainbow Suites offers boutique vacation rentals in Wilton Manors, Florida — a small collection of private king suites a short walk from Wilton Drive. They are LGBTQ+ owned and operated, pet-friendly, and bookable directly with self check-in, in the walkable heart of Greater Fort Lauderdale.",
    propertySlugs: ["wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    context: [
      {
        h: "The heart of Wilton Manors",
        body:
          "Wilton Manors is the walkable, LGBTQ+ center of Greater Fort Lauderdale, and our suites put you a short walk from Wilton Drive's dining, cafés and nightlife. Leave the car, stroll to dinner, and come home to a quiet, design-led retreat.",
      },
      {
        h: "Boutique, hosted, and honest",
        body:
          "We keep our collection small and personally host every stay — so each home is spotless and every detail considered. Choose an intimate king suite for two or the two-bedroom that sleeps four; all are pet-friendly with fast Wi-Fi and self check-in.",
      },
    ],
    faqs: [
      {
        q: "Where are Double Rainbow Suites' Wilton Manors rentals?",
        a: "They're in the heart of Wilton Manors, a short walk from Wilton Drive, and roughly 10 minutes from Fort Lauderdale Beach and 15–20 minutes from Fort Lauderdale–Hollywood International Airport (FLL).",
      },
      {
        q: "Are they good for couples?",
        a: "Yes — our king suites are ideal for couples and solo travelers, and the 2BR option comfortably sleeps four for friends or small groups.",
      },
    ],
  },
  {
    slug: "fort-lauderdale-vacation-rentals-near-beach",
    category: "Near the beach · Fort Lauderdale",
    h1: "Fort Lauderdale Vacation Rentals Near the Beach",
    metaTitle: "Fort Lauderdale Vacation Rentals Near the Beach | Double Rainbow Suites",
    metaDescription:
      "Stay minutes from Fort Lauderdale Beach. Pet-friendly boutique rentals — king suites and a private-pool home — about a 10-minute drive to the sand. Book direct.",
    answer:
      "Double Rainbow Suites offers vacation rentals about a 10-minute drive from Fort Lauderdale Beach — pet-friendly king suites in Wilton Manors and a private-pool home in Fort Lauderdale. All are bookable directly with self check-in, placing you close to the Atlantic beaches, Las Olas and the nightlife of Wilton Drive.",
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    context: [
      {
        h: "Beach days, made easy",
        body:
          "All of our homes sit roughly 2.5 miles — about a 10-minute drive — from Fort Lauderdale Beach's blue-flag Atlantic sand. Spend the day on the coast or along Las Olas, then retreat to a quiet, private home rather than a busy beachfront hotel.",
      },
      {
        h: "Beach near, crowds far",
        body:
          "Staying a few minutes inland means calmer streets, free parking and better value — while the beach, the Drive and the Intracoastal are all a short drive away. We'll point you to the quietest stretches of sand and the best local spots.",
      },
    ],
    faqs: [
      {
        q: "How far are Double Rainbow Suites from Fort Lauderdale Beach?",
        a: "About 2.5 miles — roughly a 10-minute drive — from all of our homes.",
      },
      {
        q: "Is it better to stay near the beach or in Wilton Manors?",
        a: "Our Wilton Manors suites give you walkable nightlife plus a quick drive to the beach; the Fort Lauderdale pool home gives you a private pool and yard, also about 10 minutes from the sand. Both put the beach within easy reach.",
      },
    ],
  },
  {
    slug: "fort-lauderdale-cruise-port-hotels",
    category: "Before or after your cruise · Port Everglades",
    h1: "Where to Stay Near Port Everglades Before or After a Cruise",
    metaTitle: "Where to Stay Near Port Everglades (Fort Lauderdale) Before or After a Cruise",
    metaDescription:
      "Sailing from Port Everglades? Skip the cruise-port hotel — stay in a pet-friendly vacation rental about 15 minutes from the terminals, before or after your cruise. More space, a kitchen, and time to enjoy Fort Lauderdale. Book direct.",
    answer:
      "Sailing from Port Everglades in Fort Lauderdale? Whether you need a place the night before or want to unwind for a day or two after, Double Rainbow Suites offers pet-friendly vacation rentals about a 15-minute drive from the cruise terminals — with more space, a full kitchen and better value than a hotel room. We host guests sailing with Royal Caribbean, Celebrity Cruises, Princess Cruises, Holland America Line, Disney Cruise Line and Carnival — all from Port Everglades. Homes are near Wilton Manors and Fort Lauderdale Beach, so it's easy to add a few days on either side of your cruise.",
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    context: [
      {
        h: "A vacation rental beats a cruise-port hotel",
        body:
          "Pre- and post-cruise hotels near Port Everglades are often pricey and cramped. A vacation rental gives you a full home for the same or less — a kitchen for a relaxed breakfast before you sail (or a quiet place to decompress after), room for the whole group, and a private night's sleep around early departures and late arrivals.",
      },
      {
        h: "Make it a Fort Lauderdale mini-break",
        body:
          "Don't just pass through — spend a day or two on either side of your cruise. You're about 10 minutes from Fort Lauderdale Beach and the nightlife of Wilton Drive, so you can hit the sand, explore Las Olas and enjoy the city before boarding or after you disembark. Our homes are roughly 6 miles (15 minutes) from the terminals and 7 miles from FLL airport.",
      },
    ],
    linesSection: CRUISE_LINES_SECTION,
    seasonalNote: {
      h: "Sailing for Thanksgiving?",
      body: "Thanksgiving week is one of Port Everglades' busiest departure periods — every major line runs holiday Caribbean sailings, and the terminals are packed Wednesday through Sunday. A private home 15 minutes away means a full kitchen for Thanksgiving eve, free parking while you sail, and self check-in that works around late flights and early departures. Dates book out early.",
      linkLabel: "Thanksgiving cruise guide",
      linkTo: "/thanksgiving-cruise-fort-lauderdale",
    },
    whenToBook: WHEN_TO_BOOK,
    guestReviews: CRUISE_GUEST_REVIEWS,
    keywords: CRUISE_KEYWORDS,
    ctaNote: CRUISE_CTA_NOTE,
    faqs: [
      {
        q: "Which cruise lines sail from Port Everglades?",
        a: "Royal Caribbean, Celebrity, Princess, Holland America, Disney and Carnival all sail from Port Everglades — from Royal Caribbean's Oasis-class mega-ships to Disney's dedicated family terminal. Our homes are about 15 minutes from every terminal.",
      },
      {
        q: "Where should I stay before or after a cruise from Port Everglades?",
        a: "A vacation rental a short drive from the port is ideal for both — Double Rainbow Suites' homes are about 15 minutes from the Port Everglades terminals, with kitchens, parking and self check-in for early departures and late arrivals.",
      },
      {
        q: "Is a rental better than a hotel for a cruise stay?",
        a: "For most travelers, yes — more space, a kitchen, room for the group and better value, while staying just minutes from the port and the beach.",
      },
      {
        q: "Can I add a few days in Fort Lauderdale around my cruise?",
        a: "Absolutely — we love pre- and post-cruise guests. You're about 10 minutes from Fort Lauderdale Beach and Wilton Drive, so it's easy to make a mini-break of it before or after you sail.",
      },
    ],
  },
  {
    slug: "fort-lauderdale-vacation-rentals-near-cruise-port",
    category: "Near the cruise port · Port Everglades",
    h1: "Fort Lauderdale Vacation Rentals Near the Cruise Port",
    metaTitle: "Vacation Rentals Near Port Everglades Cruise Port | Double Rainbow Suites",
    metaDescription:
      "Pet-friendly vacation rentals about 15 minutes from Port Everglades and FLL airport. Ideal before or after a cruise — kitchen, parking, self check-in, and time to enjoy the area. Book direct.",
    answer:
      "Double Rainbow Suites offers pet-friendly vacation rentals near Port Everglades in Fort Lauderdale — approximately a 15-minute drive from the cruise terminals and from Fort Lauderdale–Hollywood International Airport (FLL). We host guests sailing with Royal Caribbean, Celebrity Cruises, Princess Cruises, Holland America Line, Disney Cruise Line and Carnival — all sailing from Port Everglades. Perfect for the night before an early departure or a relaxed day or two after you disembark, with full kitchens, free parking and self check-in — and close to Wilton Manors and Fort Lauderdale Beach.",
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    context: [
      {
        h: "Built for early departures and easy arrivals",
        body:
          "Cruises leave early and return mid-morning. Staying a short drive from Port Everglades means a calm coffee before you board, and a comfortable, private place to land afterwards — far less stressful than rushing to or from a distant hotel.",
      },
      {
        h: "Stay a while — the beach and the Drive are minutes away",
        body:
          "Extend your trip on either side of the cruise. You're about 6 miles (15 minutes) from the terminals, roughly 7 miles from FLL airport, and about 10 minutes from Fort Lauderdale Beach and Wilton Drive — plenty of reason to spend a day or two enjoying the area.",
      },
    ],
    linesSection: CRUISE_LINES_SECTION,
    seasonalNote: {
      h: "Sailing for Thanksgiving?",
      body: "Thanksgiving week is one of Port Everglades' busiest departure periods — every major line runs holiday Caribbean sailings, and the terminals are packed Wednesday through Sunday. A private home 15 minutes away means a full kitchen for Thanksgiving eve, free parking while you sail, and self check-in that works around late flights and early departures. Dates book out early.",
      linkLabel: "Thanksgiving cruise guide",
      linkTo: "/thanksgiving-cruise-fort-lauderdale",
    },
    whenToBook: WHEN_TO_BOOK,
    guestReviews: CRUISE_GUEST_REVIEWS,
    keywords: CRUISE_KEYWORDS,
    ctaNote: CRUISE_CTA_NOTE,
    faqs: [
      {
        q: "Which cruise lines sail from Port Everglades?",
        a: "Royal Caribbean, Celebrity, Princess, Holland America, Disney and Carnival all sail from Port Everglades — from Royal Caribbean's Oasis-class mega-ships to Disney's dedicated family terminal. Our homes are about 15 minutes from every terminal.",
      },
      {
        q: "Are these rentals convenient for a cruise from Port Everglades?",
        a: "Yes — all of our homes are about a 15-minute drive from the Port Everglades cruise terminals, with free parking and self check-in, ideal before or after your sailing.",
      },
      {
        q: "Can I stay both before and after my cruise?",
        a: "Yes — we welcome pre- and post-cruise stays. Many guests book the night before to catch an early departure, then a night or two after to relax and explore Fort Lauderdale.",
      },
      {
        q: "How far is the airport (FLL)?",
        a: "Fort Lauderdale–Hollywood International Airport is roughly 7 miles away — about a 15–20 minute drive.",
      },
    ],
  },
  {
    slug: "thanksgiving-cruise-fort-lauderdale",
    category: "Pre-cruise stay · Thanksgiving week · Port Everglades",
    h1: "Sailing for Thanksgiving? Your Pre-Cruise Stop in Fort Lauderdale",
    metaTitle: "Thanksgiving Cruise from Port Everglades — Where to Stay in Fort Lauderdale | Double Rainbow Suites",
    metaDescription:
      "Sailing from Port Everglades over Thanksgiving? Double Rainbow Suites is 15 minutes from the terminals — a private home with a full kitchen, free parking and an easy early departure. Book direct.",
    keywords:
      "Thanksgiving cruise Fort Lauderdale, pre-cruise stay Thanksgiving Port Everglades, where to stay before Thanksgiving cruise, Port Everglades Thanksgiving weekend, cruise departure Thanksgiving Fort Lauderdale, vacation rental near cruise port Thanksgiving",
    answer:
      "Thanksgiving week is one of the busiest departure weeks of the year at Port Everglades — families boarding ships to the Caribbean, the Bahamas and beyond. If you're flying into Fort Lauderdale before your sailing, Double Rainbow Suites is about 15 minutes from the terminals, with a full kitchen, free parking and self check-in that works around early-morning departures. Skip the airport hotel. Spend Thanksgiving eve in a real home.",
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    context: [
      {
        h: "Why stay here before your Thanksgiving cruise?",
        body: "Port Everglades handles some of its highest single-day passenger volumes over Thanksgiving weekend, and the terminals are busy from early morning. A pre-cruise night near the port means no early-morning scramble from a hotel across town — just a 15-minute drive, a kitchen for your own coffee, and free off-street parking if you're leaving a car while you sail. Our homes sleep between 2 and 6 guests, and because you book direct, there are no OTA fees built into the rate.",
      },
      {
        h: "Thanksgiving week at Port Everglades",
        body: "The Wednesday before Thanksgiving and the holiday weekend itself are among the highest-volume departure days of the year. Royal Caribbean, Carnival, Celebrity, Princess, Holland America and Disney all operate holiday itineraries — typically 7-night Caribbean sailings timed to return the following Saturday. Hotels around the port price sharply upward for the week; Wilton Manors puts you 15 minutes from every terminal without the lobby queue or the markup.",
      },
      {
        h: "What's included",
        body: "Every Double Rainbow Suites home comes with the basics that matter most before a sailing:",
        list: [
          "Full kitchen — cook your own Thanksgiving eve dinner (Whole Foods is 7 minutes away; Publix even closer)",
          "Free off-street parking — leave your car while you cruise, no daily fees",
          "Keyless self check-in — arrive on your schedule, for late flights and early mornings alike",
          "Direct host contact — flight delayed? Need your ship's terminal address? Message us",
          "About 15 minutes to Port Everglades — relaxed, not rushed",
        ],
      },
    ],
    linesSection: CRUISE_LINES_SECTION,
    whenToBook: {
      h: "A note on timing",
      body: "Thanksgiving moves every year, but the port's peaks are consistent: the Wednesday evening before Thanksgiving and the Saturday and Sunday of the holiday weekend are the busiest embarkation days. Most guests flying in arrive Wednesday or Thursday — and those dates book out well ahead of the holiday. Lock in your pre-cruise night as soon as your cruise is confirmed.",
    },
    guestReviews: CRUISE_GUEST_REVIEWS,
    ctaNote: "Thanksgiving departure days book out well ahead of the holiday — secure your pre-cruise night early.",
    faqs: [
      {
        q: "Which cruise lines sail from Port Everglades at Thanksgiving?",
        a: "All of the major lines operating from Port Everglades run Thanksgiving holiday sailings — Royal Caribbean, Carnival, Celebrity, Princess, Holland America and Disney Cruise Line all offer 7-night Caribbean itineraries timed around Thanksgiving week. Check your cruise line's holiday schedule for specific departure dates.",
      },
      {
        q: "Can I leave my car while I cruise?",
        a: "Yes — all our homes include free off-street parking. Leave your car, sail, and pick it up when you return. No daily parking fees.",
      },
      {
        q: "Is there somewhere nearby to get groceries the night before?",
        a: "Whole Foods is about 7 minutes away and Publix is even closer — everything you need for a proper Thanksgiving eve dinner in your own kitchen, or to stock up on snacks for the ship.",
      },
      {
        q: "How far is it to the cruise terminals?",
        a: "About 6 miles — typically 15 minutes by car. We'll give you the specific terminal address for your sailing at check-in; terminals vary by cruise line and sometimes by vessel.",
      },
      {
        q: "What if my flight is delayed?",
        a: "Self check-in means you can arrive at any hour. Message us when you land and we'll make sure everything is ready.",
      },
    ],
  },
  {
    slug: "wilton-manors-ev-charging-vacation-rentals",
    category: "EV charging · Wilton Manors",
    h1: "Wilton Manors Vacation Rentals with Level 2 EV Charging",
    metaTitle: "Wilton Manors Vacation Rentals with EV Charging | Double Rainbow Suites",
    metaDescription:
      "Driving an EV to Fort Lauderdale? Double Rainbow Suites' Wilton Manors rentals include a Level 2 EV charger, free for guests — free parking, a short walk to Wilton Drive, 10 minutes to the beach. Book direct.",
    keywords:
      "hotel with EV charger Wilton Manors, EV charging vacation rental Fort Lauderdale, Wilton Manors rental with EV charger, Level 2 EV charger rental, EV friendly hotel Fort Lauderdale, Tesla charging Wilton Manors, vacation rental with EV charging Florida",
    answer:
      "Double Rainbow Suites offers vacation rentals in Wilton Manors, Florida with a Level 2 EV charger on-site — free for guests at all three of our homes at 1425 NE 24th St. Plug in overnight, wake up to a full charge, and spend your days on Wilton Drive (a short walk) or Fort Lauderdale Beach (about 10 minutes). Free parking, self check-in, and direct booking with no platform fees.",
    propertySlugs: ["wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    context: [
      {
        h: "Charge overnight, explore by day",
        body:
          "The Level 2 charger lives on-site at our Wilton Manors address and it's free for guests — no apps, no per-kWh surprises, no hunting for a working public charger after a long drive down I-95. Plug in when you get back for the evening and the car is ready by morning, every morning.",
      },
      {
        h: "Why a rental with EV charging beats a hotel with EV charging",
        body:
          "Hotels that advertise EV charging often mean two shared plugs in a paid garage. Here you get free off-street parking at your own front door and a Level 2 charger steps away — plus the things that make a rental better for a longer trip anyway: a kitchen, a private entrance and a quiet residential street.",
      },
      {
        h: "The EV-friendly home base for South Florida",
        body:
          "Wilton Manors sits in the middle of everything: Fort Lauderdale Beach and Las Olas in about 10 minutes, Miami in under an hour, the Keys an easy day trip. Range is never the question — you leave each morning full, and if you ever need a top-up, public fast chargers line the Federal Highway and I-95 corridors nearby.",
      },
    ],
    faqs: [
      {
        q: "Is the EV charger free for guests?",
        a: "Yes — the Level 2 charger is free for guests throughout the stay, just like the parking. No apps, no fees, no per-kWh billing.",
      },
      {
        q: "What kind of EV charger is it?",
        a: "A Level 2 charger on-site — the kind that takes most EVs from low to full overnight. Plug in when you're back for the evening and you start every day on a full charge.",
      },
      {
        q: "Which Double Rainbow Suites homes have EV charging?",
        a: "All three Wilton Manors homes at 1425 NE 24th St — the two king suites and the spacious 2BR efficiencies. (Our Fort Lauderdale private-pool home has free parking but no charger yet.)",
      },
      {
        q: "Is there parking as well as charging?",
        a: "Yes — free off-street parking at your door, plus the charger. No garage tickets, no valet, no circling.",
      },
      {
        q: "How close are the homes to Wilton Drive and the beach?",
        a: "Wilton Drive's restaurants and bars are a short walk away, and Fort Lauderdale Beach is about a 10-minute drive — you'll barely touch the charge.",
      },
    ],
  },
  {
    slug: "winter-sun-vacation-rentals-fort-lauderdale",
    category: "Winter sun · Wilton Manors",
    h1: "Winter Sun Vacation Rentals in Fort Lauderdale",
    metaTitle: "Winter Sun Vacation Rentals in Fort Lauderdale | Double Rainbow Suites",
    metaDescription:
      "Escape winter in Fort Lauderdale — boutique king suites a short walk from Wilton Drive and a private heated-pool home. Monthly winter stays welcome, pet-friendly, free EV charging. Book direct and skip platform fees.",
    keywords:
      "winter sun holiday Florida, snowbird rentals Fort Lauderdale, escape winter Florida, winter sun Wilton Manors, monthly winter rental Florida, Canadian snowbird rental Florida, winter sun from UK Florida, Florida winter getaway",
    answer:
      "Double Rainbow Suites offers winter-sun vacation rentals in Wilton Manors and Fort Lauderdale, Florida — where winter days average around 24–26°C (mid-to-high 70s °F) and rainfall is at its lowest. Choose boutique king suites a short walk from Wilton Drive or a private heated-pool home; monthly winter stays are welcome, every home is pet-friendly, and booking direct means no platform fees.",
    propertySlugs: ["wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies", "pet-friendly-pool-oasis"],
    context: [
      {
        h: "Winter here means pool weather",
        body:
          "While Toronto, London and Berlin scrape ice off the windshield, Fort Lauderdale settles into its dry season — daytime highs around 24–26°C (mid-to-high 70s °F) from December to March, low humidity and an ocean you can actually swim in. Our Private Pool Oasis adds a heated private pool to the equation, so even a rare cool evening ends in warm water.",
      },
      {
        h: "Suite comfort, hotel ease — without the hotel",
        body:
          "Our two king suites at 1425 NE 24th St are made for winter escapes: a plush king bed, private entrance and patio, a coffee bar for slow mornings, and Wilton Drive's cafés and restaurants a short walk away. Self check-in means you land at FLL and you're horizontal within the hour — no front desk, no resort fees, and free Level 2 EV charging if you drive down.",
      },
      {
        h: "Stay a week — or stay the season",
        body:
          "Snowbirds welcome: monthly and longer winter stays are easy to arrange — just tell us your dates. The two-bedroom efficiencies sleep four with a kitchenette, and the Private Pool Oasis has a full kitchen and room for six. Every home is pet-friendly, because a winter away shouldn't mean leaving anyone behind.",
      },
    ],
    whenToBook: {
      h: "When to book",
      body: "January through March is South Florida's high season, and most winter guests book one to three months ahead — the best winter weeks go first, especially around festival and event weekends. Our advice: as soon as your flights make sense, lock in your dates — and book direct. Our best rates are always right here.",
    },
    ctaNote:
      "January to March is our busiest season — winter dates sell first, especially February.",
    // REAL guest reviews only — swap in winter/snowbird-specific reviews when
    // guests mention them. Never invent one.
    guestReviews: [
      {
        name: "Jordan",
        date: "May 2026",
        text: "The private pool was the highlight — we barely left. Spotlessly clean and the host clearly thought of everything.",
      },
      {
        name: "Chris",
        date: "May 2026",
        text: "Walked everywhere on the Drive and came home to the quietest, comfiest bed. The perfect little base in Wilton Manors.",
      },
    ],
    faqs: [
      {
        q: "How warm is Fort Lauderdale in winter?",
        a: "Daytime highs run about 24–26°C (mid-to-high 70s °F) from December through March — it's the dry season, with low humidity and plenty of sun. Evenings are mild; pack a light layer.",
      },
      {
        q: "Can I book a winter rental for a month or longer?",
        a: "Yes — monthly and longer stays are welcome. Tell us your dates when you book, or message us and we'll set it up.",
      },
      {
        q: "How do I get there from Canada or Europe?",
        a: "Fly into Fort Lauderdale–Hollywood International (FLL), about 15–20 minutes from the homes — it has direct winter service from many Canadian and European cities. Miami International (MIA) is under an hour away as an alternative.",
      },
      {
        q: "Which home is best for a winter escape?",
        a: "Couples love the two king suites — walk to Wilton Drive, private patio, free EV charging. The 2BR efficiencies sleep four, and the Private Pool Oasis adds a heated private pool and full kitchen for longer family stays.",
      },
      {
        q: "Are the winter rentals pet-friendly?",
        a: "Yes — all four homes welcome dogs and cats. Add your pet during booking and the home is ready for the whole family.",
      },
    ],
  },
];

export const getLandingPage = (slug) => landingPages.find((p) => p.slug === slug);
